import React, { Fragment, useContext, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useFeature } from "@growthbook/growthbook-react";
import icons from "@paljs/icons";
import {
  Layout,
  LayoutColumn,
  LayoutColumns,
  LayoutContainer,
  LayoutContent,
} from "@paljs/ui/Layout";
import { Menu, MenuRefObject } from "@paljs/ui/Menu";
import { Sidebar, SidebarBody, SidebarRefObject } from "@paljs/ui/Sidebar";
import _ from "lodash";
import { ThemeProvider } from "styled-components";

import config from "../../config";
import { AuthContext, AuthStatus } from "../../utils/authContext";
import Header from "../header";

import {
  generalPricingMenuLinks,
  generalShopMenuLinks,
  profileMenuLinks,
  summerOfferMenuLinks,
  summerPricingMenuLinks,
  winterOfferMenuLinks,
  winterPricingMenuLinks,
} from "./menuItems";
import SimpleLayout from "./SimpleLayout";
import themes from "./themes";
import { useQuery } from "graphql-hooks";
import { GetActiveOrdersQuery, GetArchiveOrdersQuery, GetOrdersQuery } from "../../types";
import { GET_ACTIVE_ORDERS, GET_ARCHIVE_ORDERS, GET_ORDERS } from "../../data/queries";
import { ADD_DISCOUNT_CODE, ADD_MEMBERSHIP_ITEM, PLACE_ORDER, REMOVE_CART_ITEM, REMOVE_DISCOUNT_CODE, UPDATE_CHILD, UPDATE_ME } from "../../data/mutations";
import { ADD_SUMMER_CAMPS_2026_TO_CART } from "../../routes/summer2026/data";
import { ADD_WINTER_CAMPS_2027_TO_CART } from "../../routes/winter2027/data";

export const winterMenuItems = [];

type LayoutProps = {
  children: any;
  noPadding?: boolean;
};
const LayoutPage: React.FC<LayoutProps> = ({ children, noPadding }) => {
  const auth = useContext(AuthContext);

  const { loading, data, error } = useQuery<GetOrdersQuery>(GET_ORDERS, {
    refetchAfterMutations: [
      REMOVE_CART_ITEM,
      ADD_SUMMER_CAMPS_2026_TO_CART,
      ADD_WINTER_CAMPS_2027_TO_CART,
    ],
    useCache: false,
  });

  const { loading: loadingOrders, data: dataOrders, error: errorOrders, refetch } = useQuery<GetActiveOrdersQuery>(
    GET_ACTIVE_ORDERS,
    {
      refetchAfterMutations: [
        PLACE_ORDER,
      ],
      useCache: false,
    }
  );
  
  const meId = data?.me.id;
  const cart = data?.me.cart;
  const cartProblems = cart?.problems;
  const isIncomplete = data?.me.isIncomplete;
  const itemGroups = _.get(data, "me.cart.itemsGroups");
  const discounts = _.get(cart, "discounts");
  const activeOrders = dataOrders?.me.activeOrders;

  const sidebarRef = useRef<SidebarRefObject>(null);
  // const router = useRouter()
  const menuRef = useRef<MenuRefObject>(null);
  const [, setSeeHeader] = useState(true);
  const location = useLocation();

  const getStateMenuItem = (
    state?: "hidden" | "visible" | "compacted" | "expanded"
  ) => {
    setSeeHeader(state !== "compacted");
  };

  const useAuthLayout = auth.authStatus !== AuthStatus.SignedIn;

  const paddingClass = noPadding ? "no-padding" : "";
  const LayoutAny = Layout as any;

  const { show_summer_camp, show_winter_2027_camp } = config.FEATURE_TOGGLE;

  const { on: showWinterCamp } = useFeature(show_winter_2027_camp);
  const { on: showSummerCamp } = useFeature(show_summer_camp);
  const cartMenuItemIndex = generalShopMenuLinks.findIndex((item) => item.title === "Koszyk");
  const orderMenuItemIndex = generalShopMenuLinks.findIndex((item) => item.title === "W trakcie opłacania");

  if (cartMenuItemIndex >= 0 && itemGroups && itemGroups.length > 0) {
    generalShopMenuLinks[cartMenuItemIndex].extras = [
      {
        position: 'after',
        content: <span style={{ background: 'var(--chakra-colors-red-500)', color: 'white', padding: '2px 2px', borderRadius: 'var(--chakra-radii-md)' }}>{itemGroups.length}</span>,
      }
    ]
  }

  if (orderMenuItemIndex >= 0 && activeOrders && activeOrders.length > 0) {
    generalShopMenuLinks[orderMenuItemIndex].extras = [
      {
        position: 'after',
        content: <span style={{ background: 'var(--chakra-colors-red-500)', color: 'white', padding: '2px 2px', borderRadius: 'var(--chakra-radii-md)' }}>{activeOrders.length}</span>,
      }
    ]
  }

  const menuItems = _.concat(
    generalShopMenuLinks,
    showWinterCamp ? winterOfferMenuLinks : [],
    showSummerCamp ? summerOfferMenuLinks : [],
    profileMenuLinks,
    generalPricingMenuLinks,
    // No winter 2027 price list yet (SCRUM-145): winterPricingMenuLinks stays hidden
    showSummerCamp ? summerPricingMenuLinks : []
  );

  return (
    <Fragment>
      <ThemeProvider theme={themes()}>
        <Fragment>
          <SimpleLayout />

          <LayoutAny
            evaIcons={icons}
            dir={"ltr"}
            className={!useAuthLayout ? "auth-layout" : ""}
          >
            {!useAuthLayout && (
              <Header toggleSidebar={() => sidebarRef.current?.toggle()} />
            )}
            <LayoutContainer>
              {!useAuthLayout && (
                <Sidebar
                  getState={getStateMenuItem}
                  ref={sidebarRef}
                  property="start"
                  containerFixed
                  responsive
                  className="menu-sidebar"
                >
                  <SidebarBody>
                    <Menu
                      className="sidebar-menu"
                      Link={Link}
                      ref={menuRef}
                      items={menuItems}
                      currentPath={location.pathname}
                      toggleSidebar={() => sidebarRef.current?.hide()}
                    />
                  </SidebarBody>
                </Sidebar>
              )}
              <LayoutContent>
                <LayoutColumns>
                  <LayoutColumn className={`main-content ${paddingClass}`}>
                    {children}
                  </LayoutColumn>
                </LayoutColumns>
              </LayoutContent>
            </LayoutContainer>
          </LayoutAny>
        </Fragment>
      </ThemeProvider>
    </Fragment>
  );
};

export default LayoutPage;
